import { useMemo, useState } from 'react'
import {
    ColumnDef,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
    TotalEmissionsReportSettings,
    TotalEmissionsReportEntry,
} from '@/hooks'
import { formatNumber } from '@/utils/formatValueWithUnit'
import { formatPercentage } from '@/utils/formatPercentage'
import { TotalEmissionsTable } from './TotalEmissionsTable'
import { formatDateRangeForTable } from '@/utils/formatDateForTable'

export type TotalEmissionsTableContainerProps = {
    data: TotalEmissionsReportEntry[]
    isLoading?: boolean
    type: TotalEmissionsReportSettings['type']
    startDate?: string
    endDate?: string
    total?: number
}

export const TotalEmissionsTableContainer: React.FC<
    TotalEmissionsTableContainerProps
> = ({ data, isLoading = false, type, startDate, endDate, total }) => {
    const { t } = useTranslation('translations')

    const typeToFirstColKey: Record<
        TotalEmissionsReportSettings['type'],
        string
    > = {
        emissionTypes: 'sustainability.table.emission_type',
        commodities: 'sustainability.table.commodity_type',
        electricitySources: 'sustainability.table.electricity_mix_type',
    }

    const typeToGroupKey: Record<TotalEmissionsReportSettings['type'], string> =
        {
            emissionTypes: 'sustainability.table.total_emissions',
            commodities: 'sustainability.table.total_commodity',
            electricitySources: 'sustainability.table.total_electricity_mix',
        }

    const firstColHeader = t(typeToFirstColKey[type])
    const groupHeader = t(typeToGroupKey[type])

    const dateRange =
        startDate && endDate ? formatDateRangeForTable(startDate, endDate) : ''
    const groupHeaderWithDate = dateRange
        ? `${groupHeader} (${dateRange})`
        : groupHeader

    // Create summary row based on type
    const summaryCells = useMemo(() => {
        if (!total) return []

        const summaryLabel =
            type === 'emissionTypes'
                ? t('sustainability.table.summary.total_co2e')
                : t('sustainability.table.summary.total')

        return [summaryLabel, formatNumber(total), '100%']
    }, [total, type, t])

    // Sorting state: default to 'value' descending
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'value', desc: true },
    ])

    const columns = useMemo<ColumnDef<TotalEmissionsReportEntry>[]>(() => {
        const nameColumn: ColumnDef<TotalEmissionsReportEntry> = {
            id: 'name',
            accessorKey: 'name',
            header: firstColHeader,
            enableSorting: true,
            sortingFn: 'alphanumeric',
            cell: ({ cell }) => cell.getValue() as string,
        }

        const groupedColumns: ColumnDef<TotalEmissionsReportEntry>[] = [
            {
                id: 'value',
                accessorKey: 'value',
                header: t('sustainability.table.absolute_value'),
                enableSorting: true,
                enableHiding: true,
                sortingFn: 'basic',
                cell: ({ cell }) => formatNumber(cell.getValue() as number),
            },
            {
                id: 'percentage',
                accessorKey: 'percentage',
                header: t('sustainability.table.percentage'),
                enableSorting: true,
                sortingFn: 'basic',
                cell: ({ cell }) =>
                    formatPercentage(cell.getValue() as number, 3),
            },
        ]

        if (type === 'electricitySources') {
            return [nameColumn, ...groupedColumns]
        }

        return [
            nameColumn,
            {
                id: 'group',
                header: groupHeaderWithDate,
                columns: groupedColumns,
            },
        ]
    }, [firstColHeader, groupHeaderWithDate, t, type])

    const table = useReactTable({
        data: data,
        columns: columns as ColumnDef<unknown>[],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
        onSortingChange: setSorting,
        initialState: {
            columnVisibility: {
                value: type !== 'electricitySources',
            },
        },
    })

    return (
        <TotalEmissionsTable
            table={table}
            isLoading={isLoading}
            sorting={sorting}
            setSorting={setSorting}
            summaryCells={type !== 'electricitySources' ? summaryCells : []}
        />
    )
}
